import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import {
  Shield,
  AlertCircle,
  FileText,
  Bell,
  CheckCircle,
  AlertTriangle,
  Download,
  Car,
  Settings
} from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/helpers';
import { mockApi } from '../services/mockApi';

const ClienteDashboardPage = () => {
  const { user, mockData } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState(null);
  const [garantia, setGarantia] = useState(null);
  const [averias, setAverias] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [documentos, setDocumentos] = useState([]);

  const cargarDatosCliente = useCallback(async () => {
    try {
      setLoading(true);

      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const clienteData = clienteRes.data;

      if (!clienteData) {
        console.error('Cliente no encontrado para email:', user.email);
        setLoading(false);
        return;
      }

      setCliente(clienteData);

      const garantiaRes = await mockApi.getGarantiaCliente(clienteData.id);
      setGarantia(garantiaRes.data);

      const averiasRes = await mockApi.getAveriasCliente(clienteData.id);
      setAverias(averiasRes.data);

      const notifRes = await mockApi.getNotificacionesCliente(clienteData.id, { leida: false });
      setNotificaciones(notifRes.data.notificaciones);

      const docsRes = await mockApi.getDocumentosCliente(clienteData.id);
      setDocumentos(docsRes.data);

    } catch (error) {
      console.error('Error al cargar datos del cliente:', error);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    if (mockData && user) {
      cargarDatosCliente();
    }
  }, [user, mockData, cargarDatosCliente]);
// ... existing code ...

  const descargarCertificado = async () => {
    try {
      const response = await mockApi.descargarCertificadoPDF(cliente.id, garantia.garantia.id);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error al descargar certificado:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando información...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!cliente || !garantia) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-warning-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No se encontró información</h2>
            <p className="text-gray-600">No se pudo cargar la información de su garantía.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const averiasActivas = averias.filter(a => ['pendiente', 'en_curso', 'diagnosticada'].includes(a.estado));
  const estadoGarantia = garantia.garantia.vigente ? 'VIGENTE' : 'EXPIRADA';
  const colorEstado = garantia.garantia.vigente ? 'success' : 'danger';
  const proximaVencer = garantia.garantia.proximaVencer;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-secondary-500/10 to-accent-500/10 rounded-3xl blur-3xl"></div>
          <div className="relative glass-effect rounded-3xl p-8 border-2 border-white/50 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-black text-black mb-2 flex items-center gap-3">
                  Mi Garantía
                  <Shield className="w-10 h-10 text-primary-500" />
                </h1>
                <p className="text-xl text-gray-700 font-semibold">
                  Bienvenido, <span className="text-black">{cliente.nombre}</span>
                </p>
              </div>
              {notificaciones.length > 0 && (
                <div className="relative">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/cliente/notificaciones')}
                    className="relative"
                  >
                    <Bell className="w-5 h-5" />
                    <span className="absolute -top-1 -right-1 bg-danger-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {notificaciones.length}
                    </span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  Garantía {garantia.garantia.numeroGarantia}
                </h2>
                <p className="text-gray-600">Nivel: {garantia.garantia.nivelNombre}</p>
              </div>
              <div className={`px-4 py-2 rounded-full bg-${colorEstado}-100 text-${colorEstado}-700 font-bold`}>
                {estadoGarantia}
              </div>
            </div>

            {proximaVencer && (
              <div className="mb-6 p-4 bg-warning-50 border-l-4 border-warning-500 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning-600" />
                  <div>
                    <p className="font-semibold text-warning-900">Su garantía está próxima a vencer</p>
                    <p className="text-sm text-warning-700">
                      Le quedan {garantia.garantia.diasRestantes} días. Considere renovarla para mantener su cobertura.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Fecha de Alta</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(garantia.garantia.fechaAlta)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Fecha de Vencimiento</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(garantia.garantia.fechaVencimiento)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Días Restantes</p>
                <p className="text-lg font-semibold text-gray-900">
                  {garantia.garantia.diasRestantes} días
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Franquicia</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(garantia.cobertura.franquicia)}
                </p>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Vehículo Asegurado</h3>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-100 rounded-lg">
                  <Car className="w-8 h-8 text-primary-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xl font-bold text-gray-900">
                    {garantia.vehiculo.marca} {garantia.vehiculo.modelo}
                  </p>
                  <p className="text-gray-600">{garantia.vehiculo.version}</p>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-600">Matrícula</p>
                      <p className="font-semibold text-gray-900">{garantia.vehiculo.matricula}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Año</p>
                      <p className="font-semibold text-gray-900">{garantia.vehiculo.year}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Combustible</p>
                      <p className="font-semibold text-gray-900">{garantia.vehiculo.combustible}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Cobertura Incluida</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Componentes Cubiertos:</p>
                  <div className="flex flex-wrap gap-2">
                    {garantia.cobertura.componentesCubiertos.map((comp, idx) => (
                      <span key={idx} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                        {comp}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Servicios Incluidos:</p>
                  <div className="space-y-2">
                    {garantia.cobertura.serviciosIncluidos.map((serv, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-success-600" />
                        <span className="text-gray-700">{serv}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <Button variant="primary" onClick={descargarCertificado}>
                <Download className="w-4 h-4 mr-2" />
                Descargar Certificado
              </Button>
              <Button variant="outline" onClick={() => navigate('/cliente/garantia')}>
                Ver Detalles Completos
              </Button>
            </div>
          </Card>

          <div className="space-y-6">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Mis Averías</h3>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/cliente/averias', { state: { abrirFormulario: true } })}
                >
                  Reportar Avería
                </Button>
              </div>

              {averias.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-success-500 mx-auto mb-3" />
                  <p className="text-gray-600">No tiene averías registradas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-sm font-semibold text-gray-600 mb-2">
                    <div>Total</div>
                    <div>Activas</div>
                    <div>Finalizadas</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-2xl font-bold">
                    <div className="text-gray-900">{averias.length}</div>
                    <div className="text-warning-600">{averiasActivas.length}</div>
                    <div className="text-success-600">{averias.length - averiasActivas.length}</div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Averías Recientes:</p>
                    <div className="space-y-2">
                      {averias.slice(0, 3).map((averia) => (
                        <div key={averia.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-gray-900">{averia.numeroAveria}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              averia.estado === 'pendiente' ? 'bg-warning-100 text-warning-700' :
                              averia.estado === 'en_curso' ? 'bg-info-100 text-info-700' :
                              averia.estado === 'finalizada' ? 'bg-success-100 text-success-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {averia.estado}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{averia.tipo}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button 
                    variant="outline" 
                    fullWidth 
                    onClick={() => navigate('/cliente/averias')}
                  >
                    Ver Todas las Averías
                  </Button>
                </div>
              )}
            </Card>

            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Documentos</h3>
                <FileText className="w-5 h-5 text-gray-400" />
              </div>

              {documentos.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No hay documentos disponibles</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Tiene {documentos.length} documento{documentos.length !== 1 ? 's' : ''} disponible{documentos.length !== 1 ? 's' : ''}
                  </p>
                  <div className="space-y-2">
                    {documentos.slice(0, 3).map((doc) => (
                      <div key={doc.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{doc.nombre}</p>
                          <p className="text-xs text-gray-600">{doc.tipo}</p>
                        </div>
                        <Download className="w-4 h-4 text-gray-400" />
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    fullWidth 
                    onClick={() => navigate('/cliente/documentos')}
                  >
                    Ver Todos los Documentos
                  </Button>
                </div>
              )}
            </Card>

            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Notificaciones</h3>
                <Bell className="w-5 h-5 text-gray-400" />
              </div>

              {notificaciones.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-success-500 mx-auto mb-3" />
                  <p className="text-gray-600">No tiene notificaciones pendientes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Tiene {notificaciones.length} notificación{notificaciones.length !== 1 ? 'es' : ''} sin leer
                  </p>
                  <div className="space-y-2">
                    {notificaciones.slice(0, 3).map((notif) => (
                      <div key={notif.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <div className={`mt-1 w-2 h-2 rounded-full ${
                            notif.prioridad === 'alta' ? 'bg-danger-500' :
                            notif.prioridad === 'media' ? 'bg-warning-500' :
                            'bg-info-500'
                          }`}></div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">{notif.titulo}</p>
                            <p className="text-xs text-gray-600 mt-1">{notif.mensaje}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    fullWidth 
                    onClick={() => navigate('/cliente/notificaciones')}
                  >
                    Ver Todas las Notificaciones
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>

        <Card>
          <h3 className="text-lg font-bold text-gray-900 mb-4">Acciones Rápidas</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button
              variant="outline"
              className="flex flex-col items-center justify-center p-6 h-auto"
              onClick={() => navigate('/cliente/averias', { state: { abrirFormulario: true } })}
            >
              <AlertCircle className="w-8 h-8 mb-2 text-warning-600" />
              <span className="font-semibold">Reportar Avería</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col items-center justify-center p-6 h-auto"
              onClick={() => navigate('/cliente/documentos')}
            >
              <FileText className="w-8 h-8 mb-2 text-primary-600" />
              <span className="font-semibold">Mis Documentos</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col items-center justify-center p-6 h-auto"
              onClick={() => navigate('/cliente/garantia')}
            >
              <Shield className="w-8 h-8 mb-2 text-success-600" />
              <span className="font-semibold">Mi Garantía</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-col items-center justify-center p-6 h-auto"
              onClick={() => navigate('/cliente/configuracion')}
            >
              <Settings className="w-8 h-8 mb-2 text-gray-600" />
              <span className="font-semibold">Configuración</span>
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default ClienteDashboardPage;
